

/**
 * Format a date string to a readable format
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  return new Date(dateString).toLocaleDateString('de-CH', defaultOptions);
};

/**
 * Format a date string to a short format
 */
export const formatDateShort = (dateString) => {
  return formatDate(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Check if a date is in the past
 */
export const isPastDate = (dateString) => {
  if (!dateString) return false;
  return new Date(dateString) < new Date();
};

/**
 * Calculate the difference between two dates in days
 */
export const daysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end - start;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// ==========================================
// CENTRAL SEMESTER & LEHRJAHR LOGIC (SOURCE OF TRUTH)
// ==========================================

// CONSTANTS
export const SEMESTER_TYPE = {
  FALL: 'WINTER',      // Using WINTER for compatibility with existing enums, but means Fall (Herbst)
  SPRING: 'SOMMER',    // Using SOMMER for compatibility, means Spring (Frühling)
};

const PROFESSION_DURATION = {
  'INFORMATIK': 4,
  'MEDIAMATIK': 4,
  'DETAILHANDEL': 3,
  'KV': 3,
  'KUNDENDIALOG': 3,
  'DEFAULT': 4
};

/**
 * Get total years for profession
 */
export const getTotalYears = (professionType) => {
  if (typeof professionType === 'number') {
    return Math.ceil(professionType / 2);
  }
  
  // Safe check for string
  const type = (professionType && typeof professionType === 'string') 
    ? professionType.toUpperCase() 
    : 'DEFAULT';
    
  return PROFESSION_DURATION[type] || PROFESSION_DURATION['DEFAULT'];
};

/**
 * Get total semesters for profession
 */
export const getTotalSemesters = (professionType) => {
  if (typeof professionType === 'number') return professionType;
  return getTotalYears(professionType) * 2;
};

/**
 * Get Academic Year Start Year for a given date
 * (If date >= Aug 1st, academic year is that year. Else previous year)
 */
const getAcademicYearStartYear = (date) => {
    const d = new Date(date);
    const month = d.getMonth(); // 0-11
    // Aug is 7
    if (month >= 7) return d.getFullYear();
    return d.getFullYear() - 1;
};

/**
 * Is date in Fall (Aug 1 - Feb 28/29)?
 */
export const isFallSemesterDate = (date) => {
  const d = new Date(date);
  const m = d.getMonth(); // 0-11
  
  // Aug(7) -> Feb(1) is Fall
  // Mar(2) usually starts Spring
  
  if (m >= 7 || m <= 1) return true;
  return false;
};

/**
 * Get Semester Type
 */
export const getSemesterType = (date = new Date()) => {
  return isFallSemesterDate(date) ? SEMESTER_TYPE.FALL : SEMESTER_TYPE.SPRING;
};

/**
 * Get Semester Boundaries
 * baseAcademicYear: e.g. 2024 (for 2024/25)
 * semesterNumber: 1-8
 */
export const getSemesterBoundaries = (baseAcademicYear, semesterNumber) => {
    const isOdd = semesterNumber % 2 !== 0; // 1, 3, 5 -> Odd -> Fall
    const yearOffset = Math.floor((semesterNumber - 1) / 2);
    const startYear = baseAcademicYear + yearOffset;
    
    if (isOdd) {
        // Fall: Aug 1 (startYear) -> Feb 28 (startYear + 1)
        const startDate = new Date(startYear, 7, 1);
        const endDate = new Date(startYear + 1, 2, 0); // Day 0 of March is last day of Feb (28/29)
        endDate.setHours(23, 59, 59, 999);
        
        return {
            semesterNumber,
            startDate,
            endDate,
            isOdd: true,
            academicYear: startYear,
            type: SEMESTER_TYPE.FALL
        };
    } else {
        // Spring: Mar 1 (startYear + 1) -> Jul 31 (startYear + 1)
        const springYear = startYear + 1;
        const startDate = new Date(springYear, 2, 1);
        const endDate = new Date(springYear, 6, 31);
        endDate.setHours(23, 59, 59, 999);
        
        return {
            semesterNumber,
            startDate,
            endDate,
            isOdd: false,
            academicYear: startYear, 
            type: SEMESTER_TYPE.SPRING
        };
    }
};

/**
 * Calculate Progress
 */
export const calculateSemesterProgress = (startDate, endDate, currentDate = new Date()) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date(currentDate);

    if (now < start) return 0;
    if (now > end) return 100;

    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
};

// ==========================================
// PUBLIC API (Matches User Requirements)
// ==========================================

export const getLehrjahr = (startDate, currentDate = new Date(), professionType = 'DEFAULT') => {
    if (!startDate) return 0;
    
    const start = new Date(startDate);
    const now = new Date(currentDate);
    
    if (now < start) return 0;
    
    // Academic Year Logic (01.08 Switch)
    const startAcadYear = getAcademicYearStartYear(start);
    const currentAcadYear = getAcademicYearStartYear(now);
    
    const lehrjahr = (currentAcadYear - startAcadYear) + 1;
    
    const max = getTotalYears(professionType);
    if (lehrjahr > max) return max; // Clamp
    return Math.max(1, lehrjahr);
};

export const getSemesterEx = (startDate, currentDate = new Date()) => {
    // This returns absolute semester number based on date
    // 1. Find academic years diff
    const start = new Date(startDate);
    const now = new Date(currentDate);
    
    if (now < start) return 0;
    
    const startAcadYear = getAcademicYearStartYear(start);
    const currentAcadYear = getAcademicYearStartYear(now);
    
    const yearDiff = currentAcadYear - startAcadYear;
    
    // 2. Determine if Spring or Fall right now
    const isFall = isFallSemesterDate(now);
    
    // If Fall: S1, S3, S5... -> (yearDiff * 2) + 1
    // If Spring: S2, S4, S6... -> (yearDiff * 2) + 2
    
    return isFall ? (yearDiff * 2) + 1 : (yearDiff * 2) + 2;
};

export const getCurrentSemester = (classStartDate, professionType = 'DEFAULT', currentDate = new Date()) => {
    if (!classStartDate) return null;
    
    const start = new Date(classStartDate);
    const now = new Date(currentDate);
    // Be robust against timestamps, use start of day logic internally if needed, but Date compare is usually fine
    
    const baseAcademicYear = getAcademicYearStartYear(start);
    
    const maxSemesters = getTotalSemesters(professionType);
    
    // Iterate to find correct one
    for (let i = 1; i <= maxSemesters; i++) {
        const bounds = getSemesterBoundaries(baseAcademicYear, i);
        if (now >= bounds.startDate && now <= bounds.endDate) {
            return {
                ...bounds,
                isActive: true,
                progress: calculateSemesterProgress(bounds.startDate, bounds.endDate, now),
                daysUntilEnd: Math.ceil((bounds.endDate - now) / (1000 * 60 * 60 * 24))
            };
        }
    }
    
    // Check if before start
    const firstSem = getSemesterBoundaries(baseAcademicYear, 1);
    if (now < firstSem.startDate) {
        return null; // Not started
    }
    
    // If we are between semesters (e.g. Feb 13 night vs Feb 14 morning overlaps? No, exact boundaries)
    // Boundaries are consecutive: Feb 13 23:59:59 -> Feb 14 00:00:00.
    // So there is really no gap in time.
    
    // What if finished?
    const lastSem = getSemesterBoundaries(baseAcademicYear, maxSemesters);
    if (now > lastSem.endDate) {
         return {
            ...lastSem,
            isActive: false,
            isFinished: true,
            progress: 100
         };
    }
    
    return null;
};

/**
 * Get all semesters for class
 */
export const getAllSemestersForClass = (classStartDate, professionType = 'DEFAULT') => {
   if (!classStartDate) return [];
   
   const start = new Date(classStartDate);
   const baseAcademicYear = getAcademicYearStartYear(start);
   const total = getTotalSemesters(professionType);
   
   const results = [];
   for(let i=1; i<=total; i++) {
       const bounds = getSemesterBoundaries(baseAcademicYear, i);
       results.push({
           ...bounds,
           name: `Semester ${i}`,
           shortName: `S${i}`
       });
   }
   return results;
};

export const formatSemesterInfo = (semesterInfo) => {
  if (!semesterInfo) return '';
  const { semesterNumber, isOdd, academicYear, isActive } = semesterInfo;
  const type = isOdd ? 'Fall' : 'Spring'; // Labeling
  const status = isActive ? '(Active)' : '(Ended)';
  return `Semester ${semesterNumber} - ${type} ${academicYear} ${status}`;
};

/**
 * Returns FSyy (Jan-Jul) or HSyy (Aug-Dec) for a given date.
 */
export const getCurrentHalfYearSemesterCode = (date = new Date()) => {
  const d = new Date(date);
  const yy = String(d.getFullYear()).slice(-2);
  return d.getMonth() <= 6 ? `FS${yy}` : `HS${yy}`;
};

/**
 * Resolve learner current apprenticeship semester number from available fields.
 */
export const getCurrentBonusSemesterNumber = (student, date = new Date()) => {
  if (!student) return 1;

  const startDate = student.apprenticeshipStartDate || student.classStartDate;
  const professionType = student.professionType || 'DEFAULT';

  if (startDate) {
    const sem = getSemesterEx(startDate, date);
    const max = getTotalSemesters(professionType);
    return Math.max(1, Math.min(sem || 1, max));
  }

  if (student.currentLehrjahr && Number.isFinite(Number(student.currentLehrjahr))) {
    return Math.max(1, (Number(student.currentLehrjahr) * 2) - 1);
  }

  if (student.year && Number.isFinite(Number(student.year))) {
    return Math.max(1, (Number(student.year) * 2) - 1);
  }

  return 1;
};

/**
 * Find the current semester from a semesters array using date-range fields.
 * Checks startDate/endDate, start_date/end_date, validFrom/validTo on each object.
 * Returns the semester's id, or null if no semester has date fields or none match today.
 */
export const getCurrentSemesterId = (semesters, referenceDate = new Date()) => {
  if (!Array.isArray(semesters) || semesters.length === 0) return null;
  const ref = new Date(referenceDate);
  ref.setHours(0, 0, 0, 0);
  const found = semesters.find((semester) => {
    const startRaw = semester.startDate || semester.start_date || semester.validFrom || semester.valid_from;
    const endRaw = semester.endDate || semester.end_date || semester.validTo || semester.valid_to;
    if (!startRaw || !endRaw) return false;
    const start = new Date(startRaw);
    const end = new Date(endRaw);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return ref >= start && ref <= end;
  });
  return found != null ? found.id : null;
};

/**
 * Extract the ordinal semester number from a semester object.
 * Checks numeric fields first, then parses the name/label text
 * (e.g. "3. Semester", "Semester 3", "S3").
 * Returns the number or null.
 */
export const extractSemesterNumber = (semester) => {
  if (!semester) return null;
  if (semester.number != null && Number.isFinite(Number(semester.number))) return Number(semester.number);
  if (semester.semesterNumber != null && Number.isFinite(Number(semester.semesterNumber))) return Number(semester.semesterNumber);
  if (semester.semester_number != null && Number.isFinite(Number(semester.semester_number))) return Number(semester.semester_number);
  const text = String(semester.name || semester.label || '');
  // Anchor patterns so "Herbstsemester 2024" doesn't match the year as a semester number.
  const m = text.match(/^(\d+)\.\s*semester/i)    // "3. Semester"
         || text.match(/^semester\s+(\d+)/i)       // "Semester 3"
         || text.match(/^S(\d+)$/i);               // "S3"
  return m ? parseInt(m[1], 10) : null;
};

/**
 * Resolve the most likely current semester id for a learner from a semesters list.
 *
 * Priority:
 *  1. Direct currentSemesterId / semesterId field on learner (if present in list)
 *  2. Date-range match using semester startDate/endDate fields
 *  3. Calculated from apprenticeshipStartDate / classStartDate via getSemesterEx
 *     — matched first by numeric number fields, then by extracted number from name/label
 *     — clamped to highest available semester if calculated number exceeds the list
 *  4. First semester (lowest ordinal number)
 *
 * Returns a semester id (number) or null.
 */
export const resolveCurrentSemesterId = ({ learner, semesters, referenceDate = new Date() }) => {
  if (!Array.isArray(semesters) || semesters.length === 0) return null;

  // 1. Direct field on learner
  const directId =
    learner?.currentSemesterId ??
    learner?.current_semester_id ??
    learner?.activeSemesterId ??
    learner?.active_semester_id ??
    learner?.semesterId ??
    learner?.semester_id;
  if (directId != null) {
    const inList = semesters.find((s) => String(s.id) === String(directId));
    if (inList) return inList.id;
  }

  // 2. Date-range match
  const byDateRange = getCurrentSemesterId(semesters, referenceDate);
  if (byDateRange != null) return byDateRange;

  // 3. Calculate from apprenticeship / class start date
  const startRaw =
    learner?.apprenticeshipStartDate ??
    learner?.apprenticeship_start_date ??
    learner?.classStartDate ??
    learner?.class_start_date ??
    learner?.educationStartDate ??
    learner?.education_start_date ??
    learner?.entryDate ??
    learner?.entry_date;

  if (startRaw) {
    const semNum = getSemesterEx(startRaw, referenceDate);
    if (semNum > 0) {
      const numberedSemesters = semesters
        .map((s) => ({ s, n: extractSemesterNumber(s) }))
        .filter(({ n }) => n != null);

      const exact = numberedSemesters.find(({ n }) => n === semNum);
      if (exact) return exact.s.id;

      // Clamp to the highest available semester number
      const maxAvailable = Math.max(...numberedSemesters.map(({ n }) => n));
      if (maxAvailable > 0 && semNum > maxAvailable) {
        const clamped = numberedSemesters.find(({ n }) => n === maxAvailable);
        if (clamped) return clamped.s.id;
      }
    }
  }

  // 4. First semester (lowest extracted number, or first in array)
  const withNumbers = semesters
    .map((s) => ({ s, n: extractSemesterNumber(s) ?? Infinity }))
    .sort((a, b) => a.n - b.n);
  return withNumbers[0]?.s.id ?? semesters[0]?.id ?? null;
};

/**
 * Format a semester object into a human-readable English label.
 * Uses the numeric ordinal if available ("Semester 3"), otherwise falls back
 * to the raw name/label field. Appends schoolYear when present.
 */
export const formatSemesterLabel = (semester) => {
  if (!semester) return '';
  const num = extractSemesterNumber(semester);
  const base = num != null ? `Semester ${num}` : (semester.name || semester.label || semester.title || 'Unnamed semester');
  return semester.schoolYear ? `${base} · ${semester.schoolYear}` : base;
};

/**
 * Returns the semester label for display (e.g., "Semester 3 · HS25").
 * Kept for backwards compatibility; semester-based code should use the label from the API.
 */
export const getCurrentBonusSemesterLabel = (semesterNumber, semesterCode) => {
  const sem = Math.max(1, Number(semesterNumber) || 1);
  return semesterCode ? `Semester ${sem} · ${semesterCode}` : `Semester ${sem}`;
};
